



import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Avatar, Grid, Divider, Button, useTheme } from "@mui/material";
import { Modal, Input, message, Tag, Image, Tabs } from "antd";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import ApiCallService from "../../../../Service/ApiCallService";
import Spinner from "../../../../components/common/Spinner";
import { getPublicS3Url } from "../../../../utils/s3PublicUrl";
import { usePermission } from "../../../../permissions/usePermission.js";
// import theme from "../../../../theme/themeConfig";
/* -------------------- LABELS -------------------- */
const LABEL_MAP = {
  userFirstName: "First Name",
  userMiddleName: "Middle Name",
  userLastName: "Last Name",
  userImageURL: "Profile Photo",
  userDocumentVerifyURL: "Verification Document",
  whatsAppNumber: "WhatsApp Number",
  mobileNumber: "Mobile Number",
  sameAsMobileNumber: "Same as Mobile Number",
  alternateNumber: "Alternate Number",
  emailId: "Email Address",
  gender: "Gender",
  dob: "Date of Birth",
  profession: "Profession",
  Education: "Education",
  maritalStatus: "Marital Status",
  address: "Address",
  city: "City",
  pinCode: "Pincode",
  Country: "Country",
  State: "State",
  Region: "Region",
  District: "District",
  Taluka: "Taluka",
  Village: "Village",
};

const IMAGE_FIELDS = ["userImageURL", "userDocumentVerifyURL"];

const LOCATION_FIELD_NAME_MAP = {
  Country: "countryName",
  State: "stateName",
  Region: "regionName",
  District: "districtName",
  Taluka: "talukaName",
  Village: "villageName",
};

/* -------------------- GROUPS -------------------- */
const FIELD_GROUPS = {
  personalInfo: {
    title: "Personal Information",
    fields: [
      "userFirstName",
      "userMiddleName",
      "userLastName",
      "gender",
      "dob",
      "maritalStatus",
      "Education",
      "profession",
      "userImageURL",
      "userDocumentVerifyURL",
    ],
  },
  contactInfo: {
    title: "Contact Information",
    fields: [
      "emailId",
      "whatsAppNumber",
      "mobileNumber",
      "sameAsMobileNumber",
      "alternateNumber",
      "address",
      "city",
      "pinCode",
    ],
  },
  locationInfo: {
    title: "Location Information",
    fields: ["Country", "State", "Region", "District", "Taluka", "Village"],
  },
};

/* -------------------- FORMATTERS -------------------- */

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return isNaN(date)
    ? value
    : date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
};

const formatValue = (field, value) => {
  if (field === "sameAsMobileNumber") return value ? "Yes" : "No";

  if (!value) return "-";

  if (field === "dob") return formatDate(value);

  //  CASE 1: If value is object (populated)
  if (typeof value === "object") {
    const key = LOCATION_FIELD_NAME_MAP[field];
    if (key && value[key]) return value[key];
    return "-";
  }

  //CASE 2: If value is string ID → fallback to "-"
  if (typeof value === "string" && value.length > 10) {
    return "-"; // don't show raw IDs
  }

  return value;
};

const MotionBox = motion(Box);

/* -------------------- FIELD CARD -------------------- */
const ProfileFieldCard = ({ field, oldValue, newValue }) => {

  const isChanged =
    newValue !== undefined &&
    JSON.stringify(oldValue) !== JSON.stringify(newValue);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isChanged ? "#FFE58F" : "#E5E7EB",
        backgroundColor: isChanged ? "#FFFBE6" : "#FFFFFF",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {LABEL_MAP[field]}
      </Typography>

      {IMAGE_FIELDS.includes(field) ? (
        <Box mt={1} display="flex" gap={2}>
          {oldValue && <Image width={70} src={oldValue} />}
          {isChanged && newValue && (
            <>
              <Typography>→</Typography>
              <Image width={70} src={newValue} />
            </>
          )}
        </Box>
      ) : (
        <Typography sx={{ mt: 1 }}>
          {isChanged ? (
            <>
              <span
                style={{ color: "#9CA3AF", textDecoration: "line-through" }}
              >
                {formatValue(field, oldValue)}
              </span>
              <span style={{ margin: "0 8px" }}>→</span>
              <span style={{ color: "#15803D", fontWeight: 600 }}>
                {formatValue(field, newValue)}
              </span>
              <Tag color="orange" style={{ marginLeft: 8 }}>
                Updated
              </Tag>
            </>
          ) : (
            formatValue(field, oldValue)
          )}
        </Typography>
      )}
    </MotionBox>
  );
};

/* -------------------- MAIN -------------------- */
const ProfileUpdateReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canByKey } = usePermission();
  const canApproveProfileUpdate = canByKey("USER_MANAGEMENT_USER_APPROVAL_APPROVE_UPDATE");
  const canDeclineProfileUpdate = canByKey("USER_MANAGEMENT_USER_APPROVAL_DECLINE_UPDATE");

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/user/${id}`
        );
        setUser(res);
      } catch {
        message.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  const handleApprove = async () => {
    try {
      setLoading(true);
      await ApiCallService.patch(
        `${process.env.REACT_APP_BASE_URL}/${id}/approve-profileUpdate`
      );
      message.success("Profile update approved");
      navigate(-1);
    } catch {
      message.error("Failed to approve update");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning("Please enter rejection reason");
      return;
    }

    try {
      setLoading(true);
      await ApiCallService.patch(
        `${process.env.REACT_APP_BASE_URL}/${id}/reject-profileUpdate`,
        { reason: rejectReason }
      );
      message.success("Profile update rejected");
      navigate(-1);
    } catch {
      message.error("Failed to reject update");
    } finally {
      setLoading(false);
      setRejectModalOpen(false);
    }
  };

  if (loading || !user) {
    return (
      <Box mt={10} textAlign="center">
        <Spinner loading />
      </Box>
    );
  }

  const changedFields = Object.keys(user.pendingUpdates || {});

  const renderGroup = (group, showOnlyChanged = false) => {
    const visibleFields = showOnlyChanged
      ? group.fields.filter((f) => changedFields.includes(f))
      : group.fields;

    if (visibleFields.length === 0) return null;

    return (
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          {group.title}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {visibleFields.map((field) => (
            <Grid item xs={12} sm={6} lg={4} key={field}>
              <ProfileFieldCard
                field={field}
                oldValue={user[field]}
                newValue={user?.pendingUpdates?.[field]}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {/* <Box sx={{ background: "#F7F8FA", minHeight: "100vh", p: 3 }}> */}
      <Paper elevation={0} variant="pageBg" sx={{ p: 3, borderRadius: 3 }}>
        {/* HEADER */}
        <Box display="flex" alignItems="center" gap={3} mb={3}>
          <Avatar src={getPublicS3Url(user.userImageURL)} sx={{ width: 88, height: 88 }} />

          <Box>
            <Typography variant="h6" fontWeight={700}>
              {user.userFirstName} {user.userMiddleName}{" "}
              {user.userLastName}
            </Typography>
            <Typography color="text.secondary">{user.emailId}</Typography>
          </Box>

         
        </Box>

        {/* TABS */}
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane
            tab={`Changed Fields (${changedFields.length})`}
            key="1"
          >
            {Object.values(FIELD_GROUPS).map((group) =>
              renderGroup(group, true)
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="All Profile Details" key="2">
            {Object.values(FIELD_GROUPS).map((group) =>
              renderGroup(group, false)
            )}
          </Tabs.TabPane>
        </Tabs>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setRejectModalOpen(true)}
            variant="outlined"
            sx={{
              color: "error.main",
              borderColor: "error.main",
              "&:hover": {
                backgroundColor: "error.main",
                color: "#fff",
                borderColor: "error.main",
              },
            }}
          >
            Reject
          </Button>

          <Button
            onClick={handleApprove}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
            }}
            size="medium"
          >
            Approve
          </Button>
        </Box>
      </Paper>

      <Modal
        title="Reject Profile Update"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter rejection reason"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </>
    // </Box>
  );
};

export default ProfileUpdateReview;
